import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router/dom';

import { AppProviders } from '@/app/providers/AppProviders';
import { router } from '@/app/router';
import { env } from '@/shared/config/env';

import '@/styles/index.css';

/**
 * Starts the mock API before the app renders.
 *
 * The `import.meta.env.DEV` guard is load-bearing, not decorative: Vite
 * substitutes it with `false` in a production build, so the whole block —
 * including the dynamic import — is dead-code eliminated and MSW's ~400 kB
 * worker never reaches users. A runtime-only flag would still ship the chunk.
 *
 * With mocks disabled it also tears down any worker left registered by a
 * previous session — see below.
 */
async function enableMocking(): Promise<void> {
  if (!import.meta.env.DEV) return;

  if (!env.VITE_ENABLE_API_MOCKS) {
    // Turning mocks off in the env is not enough on its own: a worker
    // registered by an earlier session survives the reload and keeps serving
    // mock responses, which looks exactly like the real API returning fixtures.
    // Unregistering here is what makes the flag take effect on the next load.
    // `serviceWorker` is typed as always present but is genuinely absent in
    // non-secure contexts, so the capability is checked at runtime.
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
    return;
  }

  const { worker } = await import('@/test/msw/browser');
  await worker.start({ onUnhandledRequest: 'bypass' });
}

const container = document.getElementById('root');

if (!container) {
  throw new Error('Root element #root is missing from index.html');
}

void enableMocking().then(() => {
  createRoot(container).render(
    <StrictMode>
      <AppProviders>
        <RouterProvider router={router} />
      </AppProviders>
    </StrictMode>,
  );
});
